import React, { createContext, useCallback } from 'react';
import _get from 'lodash/get';

import { useMutation, useToggle, useSegment } from 'utils/hooks';

/**
 * @type {React.Context<{
 *  isActive: boolean,
 *  isDefault: boolean,
 *  isProcessing: boolean,
 *  onToggle: () => void
 * }>}
 */
const RefundMethodToggleContext = createContext();

export const useRefundMethodToggle = () => {
  const context = React.useContext(RefundMethodToggleContext);
  if (!context) {
    throw new Error(
      'useRefundMethodToggle must be used within a RefundMethodToggleProvider'
    );
  }
  return context;
};

export function RefundMethodToggleProvider({
  method = null,
  onSaved = () => {},
  children = null
}) {
  const { track } = useSegment();

  if (!method) {
    throw new Error('RefundMethodToggleProvider requires the method prop');
  }

  const methodId = _get(method, 'id', '');
  const isEnabled = _get(method, 'is_active', false);
  const isDefault = _get(method, 'is_default', false);
  const paymentMethod = _get(method, 'payment_method', '');
  const newState = isEnabled ? 'disable' : 'enable';

  const [isActive, toggle] = useToggle(isEnabled);

  const { mutate, isProcessing } = useMutation(
    'configure.refundMethods.root',
    'PUT',
    {
      routeParam: methodId,
      payload: { ...method, is_active: !isEnabled },
      errorMessage: `Failed to ${newState} the refund method.`,
      onError: () => toggle(),
      onSuccess: data => {
        track('FTU Wizard Refund Method Toggled', {
          method_id: methodId,
          payment_method: paymentMethod,
          is_active: !isEnabled
        });
        onSaved(data);
      }
    }
  );

  const onToggle = useCallback(() => {
    mutate();
    toggle();
  }, [mutate, toggle]);

  return (
    <RefundMethodToggleContext.Provider
      value={{
        isActive,
        isDefault,
        isProcessing,
        onToggle
      }}
    >
      {children}
    </RefundMethodToggleContext.Provider>
  );
}
